import SubscribersEmpty from "@images/empty/subscribers_empty.svg"
import SearchEmpty from "@images/empty/search_empty.svg"
import { word } from "@modules/core/utils/i18n"
import useDebounce from "@modules/hooks/useDebounce"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { useProgramBundleRights } from "@modules/programBundle/hooks/useProgramBundleRights"
import {
  useProgramBundleSubscribersSearch,
} from "@modules/programBundle/programBundleSubscribers/hooks/useProgramBundleSubscriber"
import { ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOffTypeOf } from "@modules/programs/programOnOffTypes"
import { ProgramSubscriberItem } from "@modules/programs/programSubscriber/components/programSubscriberItem"
import { getSubscriptionCountText } from "@modules/programs/programUtils"
import { EmptyView } from "@modules/ui/components/emptyView"
import { HuSearchBar } from "@modules/ui/components/huSearchBar"
import { HUText } from "@modules/ui/components/huText"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { useState } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import { useTheme } from "styled-components"

type ProgramBundleInfoSubscribersListProps = {
  programBundle: ProgramBundleEntity
}
export const ProgramBundleInfoSubscribersList: React.FC<ProgramBundleInfoSubscribersListProps> = ({
  programBundle,
}) => {
  const { isAuthor } = useProgramBundleRights(programBundle)
  const theme = useTheme()
  const [searchTerms, setSearchTerms] = useState<string>("")
  const debouncedSearchTerms = useDebounce(searchTerms, 300)
  const {
    data: { content: subscribers, totalSize },
    isLoading: isLoadingSubscribers,
    isFetching,
    fetchNextPage,
  } = useProgramBundleSubscribersSearch(programBundle.id, debouncedSearchTerms)

  return (
    <div className="flex flex-column flex-grow-1 mt-4">
      <HUText fontStyle="BXS" color={theme.neutralColor900} className="mb-3">
        {getSubscriptionCountText(
          programBundle.subscriptionCount,
          isAuthor,
          false,
          true,
          programBundle.type === ProgramBundleType.ON_OFF ? ProgramOnOffTypeOf : ProgramInfiniteTypeOf,
        ).toUpperCase()}
      </HUText>
      <HuSearchBar
        placeholder={word("program.subscribersList.search.placeholder")}
        searchTerm={searchTerms}
        setSearchTerm={setSearchTerms}
      />

      {isLoadingSubscribers ? <LoadingSpinner /> : null}

      {subscribers.length > 0 ? (
        <InfiniteScroll
          dataLength={subscribers.length}
          next={fetchNextPage}
          hasMore={subscribers.length < (totalSize || 0)}
          scrollableTarget={"sidebar-content"}
          loader={<HUText fontStyle="BM">{word("global.loading")}</HUText>}
          style={{ paddingBottom: 140 }}
        >
          {subscribers.map((subscriber) => (
            <ProgramSubscriberItem
              program={programBundle}
              subscriber={subscriber}
              key={subscriber.id}
              canSeeSubscriberDetails={programBundle.type === ProgramBundleType.VIRTUAL}
            />
          ))}
        </InfiniteScroll>
      ) : !isLoadingSubscribers && !isFetching && searchTerms.length > 0 ? (
        <EmptyView
          image={{ src: SearchEmpty }}
          title={word("explore.search.no_result.title", { searchTerms })}
          description={word("explore.search.no_result.message")}
          flexGrow={false}
          textAlign="left"
          imagePosition="after"
          style={{
            backgroundColor: theme.mainWhite,
            display: "flex w-full p-0",
          }}
        />
      ) : !isLoadingSubscribers && debouncedSearchTerms?.length === 0 ? (
        <EmptyView
          image={{ src: SubscribersEmpty }}
          title={
            programBundle.type === ProgramBundleType.ON_OFF
              ? word("program.onOff.subscribers.title.empty")
              : word("program.subscribers.title.empty")
          }
          description={word("program.subscribers.description.empty")}
          flexGrow={false}
          className="p-6"
          style={{ backgroundColor: theme.mainWhite }}
        />
      ) : null }
    </div>
  )
}
